/**
 * ShapBar — Horizontal bar chart for SHAP feature attributions
 *
 * Design spec: docs/plans/2026-06-25-redesign-design-spec.md
 *   - Positive attribution = var(--color-brand) (#FF6600)
 *   - Negative attribution = muted gray (var(--color-text-muted))
 *   - Feature name + attribution value displayed
 *   - Monetary values via FinancialValue component
 *   - No green except for 'done' status
 *   - No forbidden colors (gold/teal/blue/purple/red)
 *
 * Phase 1.5-shap — SHAP explainability surfacing
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { FinancialValue } from '@/components/shared/FinancialValue';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShapBarItem {
  /** Feature name (e.g., 'transaction_amount') */
  feature: string;
  /** SHAP attribution value */
  attribution: number;
  /** Human-readable feature description */
  description?: string;
  /** Whether this value is monetary */
  isMonetary?: boolean;
}

export interface ShapBarProps {
  /** SHAP attribution items to display */
  items: ShapBarItem[];
  /** Maximum number of items to show (default: 10) */
  maxItems?: number;
  /** Title for the chart section */
  title?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show feature descriptions */
  showDescriptions?: boolean;
  /** Height of each bar row in pixels (default: 40) */
  rowHeight?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShapBar({
  items,
  maxItems = 10,
  title,
  className,
  showDescriptions = false,
  rowHeight = 40,
}: ShapBarProps) {
  // Filter to max items and calculate scale
  const displayItems = items.slice(0, maxItems);

  if (displayItems.length === 0) {
    return (
      <div
        className={cn(
          'rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6',
          className
        )}
      >
        <p className="text-sm text-[var(--color-text-muted)] text-center">
          No attribution data available
        </p>
      </div>
    );
  }

  // Find max absolute value for scaling (ensure at least 1 to avoid division by zero)
  const maxAbsValue = Math.max(
    1,
    ...displayItems.map((item) => Math.abs(item.attribution))
  );

  // Scale factor: max bar width is 60% of container
  const getBarWidth = (value: number) => {
    const percentage = (Math.abs(value) / maxAbsValue) * 60;
    return `${percentage}%`;
  };

  return (
    <div
      className={cn(
        'rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] overflow-hidden',
        className
      )}
    >
      {title && (
        <div className="border-b border-[var(--color-border-sub)] px-4 py-3">
          <h4
            className="text-sm font-medium"
            style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              color: 'var(--color-text-primary)',
            }}
          >
            {title}
          </h4>
        </div>
      )}

      <div className="py-2">
        {displayItems.map((item, index) => {
          const isPositive = item.attribution >= 0;
          const barWidth = getBarWidth(item.attribution);

          return (
            <div
              key={`${item.feature}-${index}`}
              className="flex items-center gap-3 px-4 hover:bg-[var(--color-bg-hover)] transition-colors"
              style={{ height: rowHeight }}
            >
              {/* Feature name */}
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm truncate"
                  style={{
                    fontFamily: 'Outfit, system-ui, sans-serif',
                    color: 'var(--color-text-secondary)',
                  }}
                  title={item.description || item.feature}
                >
                  {item.feature}
                </p>
                {showDescriptions && item.description && (
                  <p
                    className="text-xs truncate"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    {item.description}
                  </p>
                )}
              </div>

              {/* Bar visualization */}
              <div className="flex-1 flex items-center justify-center relative">
                {/* Center line */}
                <div
                  className="absolute h-px w-full"
                  style={{ backgroundColor: 'var(--color-border-sub)' }}
                />

                {/* Attribution bar */}
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: barWidth,
                    backgroundColor: isPositive
                      ? 'var(--color-brand)'
                      : 'var(--color-text-muted)',
                    marginLeft: isPositive ? '50%' : `calc(50% - ${barWidth})`,
                    opacity: 0.85,
                  }}
                />
              </div>

              {/* Attribution value */}
              <div className="w-24 text-right">
                {item.isMonetary ? (
                  <FinancialValue
                    value={item.attribution}
                    size="sm"
                    className="text-[var(--color-text-secondary)]"
                  />
                ) : (
                  <span
                    className="font-mono text-xs tabular-nums"
                    style={{
                      fontFamily: 'DM Mono, ui-monospace, monospace',
                      color: isPositive
                        ? 'var(--color-text-secondary)'
                        : 'var(--color-text-muted)',
                    }}
                  >
                    {item.attribution > 0 ? '+' : ''}
                    {item.attribution.toFixed(4)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        className="border-t border-[var(--color-border-sub)] px-4 py-2 flex items-center justify-center gap-6"
        style={{ backgroundColor: 'var(--color-bg-base)' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: 'var(--color-brand)' }}
          />
          <span
            className="text-xs"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Increases flag probability
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: 'var(--color-text-muted)' }}
          />
          <span
            className="text-xs"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Decreases flag probability
          </span>
        </div>
      </div>
    </div>
  );
}

export default ShapBar;
